import Hero from "@bmi-digital/components/hero";
import Section from "@bmi-digital/components/section";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { graphql } from "gatsby";
import React from "react";
import { useTheme } from "@mui/material/styles";
import BackToResults from "../components/BackToResults";
import Breadcrumbs from "../components/Breadcrumbs";
import ContactTopics from "../components/ContactTopics";
import IframeSection from "../components/IframeSection";
import Locations from "../components/Locations";
import Page from "../components/Page";
import Sections from "../components/Sections";
import NextBestActions from "../components/next-best-actions/NextBestActions";
import { updateBreadcrumbTitleFromPayload } from "../utils/breadcrumbUtils";
import createImageProps from "../components/image/createImageProps";
import createVideoProps from "../components/video/createVideoProps";
import type { NextBestActionsData as NextBestActionsData } from "../components/next-best-actions/NextBestActions";
import type { PageData } from "../components/Page";
import type { Data as LocationsData } from "../components/Locations";
import type { IframeData } from "../components/IframeSection";
import type { Data as ContactTopicsData } from "../components/ContactTopics";
import type { ContactUsPageInfoHero } from "../components/PageInfo";
import type { HomePageData, SiteData } from "../components/Site";
import type { PayloadContactUsPage } from "../types/payload";
import type { MicroCopy as MicroCopyData } from "../components/MicroCopy";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { HouseType } from "../components/visualiser/Types";
import type { GeneralErrorData } from "./general-error";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";
import type { CardCollectionSectionData } from "../components/CardCollectionSection";
import type { ServiceLocatorSectionData } from "../components/service-locator-section";
import type { TabsOrAccordionSectionData } from "../components/TabsOrAccordionSection";
import type { SignupBlockSectionData } from "../components/SignupBlock";
import type { EmbeddedScriptSectionData } from "../components/EmbeddedScriptSection";

export type Data = Pick<
  PayloadContactUsPage,
  "queriesTitle" | "queriesSubtitle" | "locationsTitle" | "breadcrumbTitle"
> &
  PageData &
  ContactUsPageInfoHero & {
    contactTopics: readonly ContactTopicsData[] | null;
    sections:
      | readonly (
          | CardCollectionSectionData
          | ServiceLocatorSectionData
          | TabsOrAccordionSectionData
          | SignupBlockSectionData
          | EmbeddedScriptSectionData
        )[]
      | null;
    locations: readonly LocationsData[] | null;
    iframe: [IframeData] | null;
    nextBestActions: readonly { value: NextBestActionsData }[] | null;
  };

export type Props = {
  data: {
    payloadContactUsPage: Data;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

const ContactUsPage = ({ data, pageContext }: Props) => {
  const {
    title,
    subtitle,
    featuredMedia,
    queriesTitle,
    queriesSubtitle,
    contactTopics,
    signupBlock,
    sections,
    locationsTitle,
    locations,
    iframe,
    breadcrumbs,
    breadcrumbTitle,
    meta,
    featuredVideo,
    nextBestActions
  } = data.payloadContactUsPage;
  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(
    breadcrumbs,
    breadcrumbTitle
  );
  const pageData: PageData = {
    breadcrumbs: enhancedBreadcrumbs,
    signupBlock,
    meta,
    path: data.payloadContactUsPage.path
  };
  const theme = useTheme();

  return (
    <Page
      title={title}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={featuredMedia?.sizes.heroDesktop.url}
    >
      <Hero
        level={1}
        title={title}
        media={
          featuredVideo
            ? createVideoProps(featuredVideo, theme)
            : featuredMedia
              ? createImageProps(
                  {
                    ...featuredMedia,
                    size: "cover",
                    loading: "eager"
                  },
                  theme
                )
              : undefined
        }
        breadcrumbs={
          <BackToResults isDarkThemed data-testid="breadcrumbs-section-top">
            <Breadcrumbs
              data={enhancedBreadcrumbs}
              isDarkThemed
              data-testid="contact-us-page-breadcrumbs-top"
            />
          </BackToResults>
        }
      >
        {subtitle}
      </Hero>

      {contactTopics?.length ? (
        <Section
          backgroundColor="pearl"
          overflowVisible
          data-testid={`contact-us-page-queries-section-${replaceSpaces(
            queriesTitle
          )}`}
        >
          {queriesTitle && <Section.Title>{queriesTitle}</Section.Title>}
          {queriesSubtitle && (
            <Typography
              variant="h4"
              component="p"
              data-testid="contact-topics-subtitle"
            >
              {queriesSubtitle}
            </Typography>
          )}

          <div style={{ marginTop: "40px" }}>
            <ContactTopics topics={contactTopics} />
          </div>
        </Section>
      ) : undefined}

      {iframe && iframe[0] && <IframeSection data={iframe[0]} />}
      {sections && <Sections data={sections} />}
      {locations?.length ? (
        <Section
          backgroundColor="white"
          data-testid={`contact-us-page-locations-section-${replaceSpaces(
            queriesTitle
          )}`}
        >
          {locationsTitle && <Section.Title>{locationsTitle}</Section.Title>}
          <div>
            <Locations data={locations} />
          </div>
        </Section>
      ) : undefined}

      {nextBestActions && <NextBestActions data={nextBestActions} />}
      <Section
        backgroundColor="alabaster"
        isSlim
        data-testid="breadcrumbs-section-bottom"
      >
        <Breadcrumbs
          data={breadcrumbs}
          data-testid="contact-us-page-breadcrumbs-bottom"
        />
      </Section>
    </Page>
  );
};

export default ContactUsPage;

export const pageQuery = graphql`
  query ContactUsPageById($pageId: String!) {
    payloadContactUsPage(id: { eq: $pageId }) {
      ...PageInfoHeroFragment
      queriesTitle
      queriesSubtitle
      contactTopics {
        ...ContactTopicsFragment
      }
      ...PayloadContactUsPageFragment
      breadcrumbTitle
      sections {
        ... on PayloadCardCollectionSection {
          ...CardCollectionSectionFragment
        }
        ... on PayloadServiceLocatorSection {
          ...ServiceLocatorSectionFragment
        }
        ... on PayloadTabsOrAccordionSection {
          ...TabsOrAccordionSectionFragment
        }
        ... on PayloadSignupBlock {
          ...SignupBlockSectionFragment
        }
        ... on PayloadEmbeddedScriptSection {
          ...EmbeddedScriptSectionFragment
        }
      }
      locationsTitle
      locations {
        ...LocationsFragment
      }
      iframe {
        ...IframeFragment
      }
      nextBestActions {
        value {
          ...NextBestActionsFragment
        }
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
